import { InvestigationPredicate } from "../../lusca-search-api/generated-src";
import {
  NO_OWNER_FILTERS,
  UNINVESTIGATED_FILTERS,
} from "./builtInViewSnapshots";

/**
 * Pre-created entries in the dashboard's Views dropdown. Unlike saved views (a snapshot of one
 * dashboard's filter groups), these are expressed as column → value(s) and applied through the
 * dashboard's own category filters (`planRuleFilters`), after resetting it to its published
 * defaults. QuickSight analyses are not source-controlled, so each column lists the names the
 * dashboard may use for it (the dataset column or a calculated field over it) and each value is
 * given in the casings the dataset may carry; names the dashboard has no filter for are skipped.
 */
export interface BuiltInDashboardView {
  id: string;
  name: string;
  description: string;
  filters: string;
  rule: InvestigationPredicate[];
}

export const BUILT_IN_VIEW_PREFIX = "__builtin__:";

/** Names the "is it investigated" filter may be on (dataset column, or a calculated field). */
const STATE_COLUMNS = ["investigation_state", "investigation", "investigated"];
/** Names the owner filter may be on. */
const OWNER_COLUMNS = ["investigation_owner", "owner"];
/** Owner label for an investigated row whose case has no owner (dataset SQL coalesce). */
export const NO_OWNER_LABEL = "No owner";

const anyOf = (columns: string[], values: string[]): InvestigationPredicate[] =>
  // Field names outside the rule allowlist are fine here: they are only matched against the
  // dashboard's filter column names, never sent to the backend.
  columns.map(
    (field) =>
      ({ field, operator: "in", values } as unknown as InvestigationPredicate)
  );

export const BUILT_IN_VIEWS: BuiltInDashboardView[] = [
  {
    id: `${BUILT_IN_VIEW_PREFIX}uninvestigated`,
    name: "Uninvestigated",
    description: "DLQ items no investigation covers",
    filters: UNINVESTIGATED_FILTERS,
    rule: anyOf(STATE_COLUMNS, ["Uninvestigated"]),
  },
  {
    id: `${BUILT_IN_VIEW_PREFIX}no-owner`,
    name: "Investigations without an owner",
    description: "Investigated items whose case needs an owner",
    filters: NO_OWNER_FILTERS,
    rule: [
      ...anyOf(STATE_COLUMNS, ["Investigated"]),
      ...anyOf(OWNER_COLUMNS, [NO_OWNER_LABEL]),
    ],
  },
];

export function builtInView(id: string): BuiltInDashboardView | undefined {
  return BUILT_IN_VIEWS.find((v) => v.id === id);
}

/**
 * Which of the view's columns the dashboard had no filter for, given `planRuleFilters`'
 * unmatched field names (one per alias): a column is missing only when none of its names matched.
 */
export function unmatchedViewColumns(
  view: BuiltInDashboardView,
  unmatchedFields: string[]
): string[] {
  const missing = new Set(unmatchedFields);
  const used = new Set(view.rule.map((p) => p.field as string));
  const groups: Array<[string, string[]]> = [
    ["investigation state", STATE_COLUMNS],
    ["investigation owner", OWNER_COLUMNS],
  ];
  return groups
    .filter(([, names]) => names.some((n) => used.has(n)))
    .filter(([, names]) => names.every((n) => missing.has(n)))
    .map(([label]) => label);
}
